/**
 * WHO-5688. Transport seam for the Pro Buyer application.
 *
 * WHY NOT APOLLO
 * Same reason as `src/features/pro-buyer-discount/data/trade-discount-client.ts`:
 * codegen validates every `gql` document against the LIVE production gateway
 * schema, and `me.proBuyer` / `submitProBuyerApplication` are not there yet.
 * The operations are plain strings sent through `dashboardRequest`.
 *
 * Once the gateway fields are on production, move these to `.graphql`
 * documents under `src/Apollo/` and delete this file.
 */
import {
  DashboardResult,
  dashboardRequest,
} from "@/features/account-dashboard/data/dashboard-client";

export type ProBuyerApplicationErrorCode =
  | "INVALID_INPUT"
  | "ALREADY_PRO_BUYER"
  | "RATE_LIMITED"
  | "SUBMIT_FAILED"
  | "FEATURE_DISABLED";

const ERROR_CODES: readonly ProBuyerApplicationErrorCode[] = [
  "INVALID_INPUT",
  "ALREADY_PRO_BUYER",
  "RATE_LIMITED",
  "SUBMIT_FAILED",
  "FEATURE_DISABLED",
];

export type ProBuyerApplicationInput = {
  companyName: string;
  registrationNumber: string;
  website: string;
};

export type SubmitProBuyerApplicationResult =
  | { ok: true }
  | { ok: false; code: ProBuyerApplicationErrorCode };

type MeProBuyerData = { me: { proBuyer: boolean | null } | null };

type SubmitProBuyerApplicationData = {
  submitProBuyerApplication: { ok: boolean } | null;
};

/* Plain strings, no `gql` tag: graphql-tag-pluck would extract a template. */
export const ME_PRO_BUYER_OPERATION = "query MeProBuyer { me { proBuyer } }";

export const SUBMIT_PRO_BUYER_APPLICATION_OPERATION =
  "mutation SubmitProBuyerApplication(" +
  "$companyName: String!, $registrationNumber: String!, $website: String!) {" +
  " submitProBuyerApplication(companyName: $companyName," +
  " registrationNumber: $registrationNumber, website: $website) { ok }" +
  " }";

const isErrorCode = (value: unknown): value is ProBuyerApplicationErrorCode =>
  typeof value === "string" &&
  (ERROR_CODES as readonly string[]).includes(value);

/**
 * `extensions.code` first; the message text as a fallback, the way step 1
 * recovers its codes. Anything unknown (transport, timeout, schema not yet
 * deployed) is a failed send: the user can retry.
 */
export const proBuyerErrorCode = (
  result: Extract<DashboardResult<unknown>, { ok: false }>
): ProBuyerApplicationErrorCode => {
  if (isErrorCode(result.code)) {
    return result.code;
  }

  const match = result.message?.match(new RegExp(ERROR_CODES.join("|")));

  return match && isErrorCode(match[0]) ? match[0] : "SUBMIT_FAILED";
};

/** True, false, or null when the gateway cannot tell (signed out, not deployed). */
export const fetchMeProBuyer = async (
  token: string | null
): Promise<boolean | null> => {
  const result = await dashboardRequest<MeProBuyerData>({
    operation: ME_PRO_BUYER_OPERATION,
    token,
  });

  if (!result.ok) {
    return null;
  }

  return result.data.me?.proBuyer ?? null;
};

export const submitProBuyerApplication = async (
  token: string | null,
  input: ProBuyerApplicationInput
): Promise<SubmitProBuyerApplicationResult> => {
  const result = await dashboardRequest<SubmitProBuyerApplicationData>({
    operation: SUBMIT_PRO_BUYER_APPLICATION_OPERATION,
    variables: input,
    token,
  });

  if (!result.ok) {
    return { ok: false, code: proBuyerErrorCode(result) };
  }

  if (!result.data.submitProBuyerApplication?.ok) {
    return { ok: false, code: "SUBMIT_FAILED" };
  }

  return { ok: true };
};
